import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { useLoginParams } from '@/src/domains/auth/hooks/useLoginParams'
import { isLoginRoute } from '@/src/domains/auth/utils/isLoginRoute'

import { LOCALE } from '../../constants/locale.constants'
import UserActions from '../../redux/UserRedux'
import Loader from '../Loader'

type Props = { isDiagnosis: boolean; emphasizeSocials?: boolean; children?: React.ReactNode }

const GoogleLoginButton: React.FC<Props> = ({ emphasizeSocials = false, isDiagnosis }) => {
  const dispatch = useDispatch()
  const router = useRouter()

  const { query } = router
  const [isPending, setIsPending] = useState(false)
  const buttonRef = useRef<HTMLDivElement>(null)

  const { therapistId } = router.query as Record<string, any>
  const { redirectUrl } = useLoginParams()

  // Inject the GSI script once — recreating it on re-render would strand the button effect's load listener.
  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.id = 'google-client-script'
    document.querySelector('body')?.appendChild(script)

    return () => {
      window?.google?.accounts.id.cancel()
      document.getElementById('google-client-script')?.remove()
    }
  }, [])

  useEffect(() => {
    window.handleCredentialResponse = (googleUser: any) => {
      setIsPending(true)
      const regex = /companyEmployee\[email\]=([^&]+)/
      const companyEmail = decodeURIComponent(router.asPath).match(regex)?.[1]

      dispatch(
        UserActions.loginRequestGoogle(
          googleUser.credential,
          Number.parseInt(therapistId, 10),
          isDiagnosis,
          redirectUrl,
          isLoginRoute(router.pathname) ? true : undefined,
          ...(query['companyEmployee[token]']
            ? [
                {
                  slug: query['companyEmployee[slug]'],
                  companyEmail,
                  activationLinkId: query['companyEmployee[activationLinkId]'],
                  blinkClientId: query['companyEmployee[blinkClientId]'],
                  token: query['companyEmployee[token]'],
                  code: query['companyEmployee[code]'],
                },
              ]
            : []),
        ),
      )
    }
    // redirectUrl no longer comes from router.query, so router identity alone can't keep the
    // registered callback in sync with it
  }, [router, redirectUrl])

  // Render the GSI button imperatively via initialize()/renderButton() in both layouts — never the
  // declarative g_id_signin widget (mixing the two breaks clicks). When socials are emphasized the
  // button is clamped to its container and re-renders on resize; otherwise it keeps GSI's auto width.
  useEffect(() => {
    const el = buttonRef.current
    if (!el) return undefined

    const renderGoogleButton = () => {
      if (!window.google?.accounts?.id) return
      el.replaceChildren()
      window.google.accounts.id.renderButton(el, {
        type: 'standard',
        shape: 'rectangular',
        theme: 'outline',
        text: 'continue_with',
        size: 'large',
        logo_alignment: 'center',
        locale: LOCALE,
        ...(emphasizeSocials ? { width: Math.max(200, Math.min(el.clientWidth, 400)) } : {}),
      })
    }

    const setup = () => {
      if (!window.google?.accounts?.id) return
      window.google.accounts.id.initialize({
        client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? '',
        callback: (response: { credential: string }) => window.handleCredentialResponse(response),
        context: 'signin',
        ux_mode: 'popup',
        itp_support: true,
      })
      renderGoogleButton()
    }

    // Only the emphasized layout clamps to the container, so only it needs to react to resizes.
    let timeout: ReturnType<typeof setTimeout> | undefined
    let observer: ResizeObserver | undefined
    if (emphasizeSocials) {
      let lastWidth = el.clientWidth
      observer = new ResizeObserver(() => {
        if (el.clientWidth === lastWidth) return
        lastWidth = el.clientWidth
        clearTimeout(timeout)
        timeout = setTimeout(renderGoogleButton, 150)
      })
      observer.observe(el)
    }

    const script = document.getElementById('google-client-script')
    if (window.google?.accounts?.id) setup()
    else script?.addEventListener('load', setup)

    return () => {
      clearTimeout(timeout)
      observer?.disconnect()
      script?.removeEventListener('load', setup)
    }
  }, [emphasizeSocials])

  /* Google Buttons doc https://developers.google.com/identity/gsi/web/guides/personalized-button */
  return (
    <>
      <Loader color="light-gray" visible={isPending} />
      <div ref={buttonRef} className={emphasizeSocials ? 'flex w-full justify-center' : undefined} />
    </>
  )
}

export default GoogleLoginButton
